const ORDERED_LINE_PATTERN = /^(\s*)(\d+)([\.\)])(\s+)(.*)$/;

function isOrderedListLine(line: string): boolean {
  return ORDERED_LINE_PATTERN.test(line);
}

/**
 * True when another numbered list line exists before/after this index,
 * allowing blank lines between items.
 */
export function hasSiblingOrderedItem(lines: string[], index: number): boolean {
  for (let i = index - 1; i >= 0; i--) {
    if (lines[i].trim() === '') continue;
    if (isOrderedListLine(lines[i])) return true;
    break;
  }

  for (let i = index + 1; i < lines.length; i++) {
    if (lines[i].trim() === '') continue;
    if (isOrderedListLine(lines[i])) return true;
    break;
  }

  return false;
}

/**
 * Detect ordered lists including items separated by blank lines.
 */
export function hasOrderedListMarkdown(text: string): boolean {
  const lines = text.split('\n');
  return lines.some((line, index) => isOrderedListLine(line) && hasSiblingOrderedItem(lines, index));
}

function convertInlineNumberedSequence(line: string): string[] | null {
  const inlineListMatch = line.match(/^(.+?):\s*(\d+)\)\s+(.+)$/);
  if (!inlineListMatch) return null;

  const [, prefix, firstNum, restOfLine] = inlineListMatch;
  const hasMultipleItems = /[,\s]+\d+\)\s+/.test(restOfLine);
  if (!hasMultipleItems) return null;

  const items: string[] = [];
  const currentText = `${firstNum}) ${restOfLine}`;
  const parts = currentText.split(/(?:,\s*|\s+)(?=\d+\)\s)/);

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const cleaned = trimmed
      .replace(/,\s*$/, '')
      .replace(/\s+(eller|or)\s*$/, '');
    items.push(cleaned.replace(/^(\d+)\)/, '$1.'));
  }

  if (items.length <= 1) return null;

  return [`${prefix}:`, ...items.map(item => `   ${item}`)];
}

/**
 * Normalizes agent/customer markdown for stable GFM rendering in chat bubbles.
 */
export function normalizeMarkdownForChat(text: string): string {
  if (!text) return text;

  const normalized = text.replace(/\\n/g, '\n');
  const lines = normalized.split('\n');
  const result: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const inlineConverted = convertInlineNumberedSequence(line);
    if (inlineConverted) {
      result.push(...inlineConverted);
      continue;
    }

    if (line.trim() === '' && i > 0 && i < lines.length - 1) {
      const prevOrdered = isOrderedListLine(lines[i - 1]);
      let nextIdx = i + 1;
      while (nextIdx < lines.length && lines[nextIdx].trim() === '') nextIdx++;
      const nextOrdered = nextIdx < lines.length && isOrderedListLine(lines[nextIdx]);
      if (prevOrdered && nextOrdered) {
        continue;
      }
    }

    const listMatch = line.match(ORDERED_LINE_PATTERN);
    if (listMatch) {
      const [, indent, num, delimiter, spacing, rest] = listMatch;
      if (hasSiblingOrderedItem(lines, i)) {
        const normalizedDelimiter = delimiter === ')' ? '.' : delimiter;
        result.push(`${indent}${num}${normalizedDelimiter}${spacing}${rest}`);
      } else {
        result.push(`${indent}${num}\\${delimiter}${spacing}${rest}`);
      }
      continue;
    }

    result.push(line);
  }

  return result.join('\n');
}
